import React, { useState } from "react";
import { Button } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { ButtonLoading } from "go-form";
import useFlash from "go-alert/AlertMessage";
import handleError from "go-app/services/errors";
import { FormDirty } from "go-form/components/FormDirty";
import { useConfirmation } from "go-form/components/ModalConfirm";
import { adminApi } from "../../../../../../../../../../services/Api/Admin/adminApi";
import {
	AnomaliesPartnerSettlementApi,
	WrongInvoiceApi,
} from "../../../../../../../../../../services/Api/Admin/adminTypes";
import SyncCompaniesModalForm from "../SyncCompaniesModalForm";
import AdminToolsAnomaliesPartnerSettlements from "./AdminToolsAnomaliesPartnerSettlements";
import AdminToolsWrongInvoicesTable from "./AdminToolsWrongInvoicesTable";

const AdminToolsForm = () => {
	const { t } = useTranslation("admin");
	const { addFlash, addSuccessFlash } = useFlash();
	const [fixPartnerLoading, setFixPartnerLoading] = useState(false);
	const [wrongInvoicesLoading, setWrongInvoicesLoading] = useState(false);
	const [wrongInvoices, setWrongInvoices] = useState<WrongInvoiceApi[] | undefined>();
	const [anomaliesPartnerSettlementsLoading, setAnomaliesPartnerSettlementsLoading] = useState(false);
	const [anomaliesPartnerSettlements, setAnomaliesPartnerSettlements] = useState<
		AnomaliesPartnerSettlementApi[] | undefined
	>();
	const [fixCompanyNameLoading, setFixCompanyNameLoading] = useState(false);
	const [showSyncCompaniesModal, setShowSyncCompaniesModal] = useState<boolean>(false);
	const [clearCacheLoading, setClearCacheLoading] = useState<boolean>(false);
	const [downloadChargebeeLoading, setDownloadChargebeeLoading] = useState<boolean>(false);
	const [updateCFLoading, setUpdateCFLoading] = useState<boolean>(false);
	const [rebuildPriceListItemCFLoading, setRebuildPriceListItemCFLoading] = useState<boolean>(false);
	const [setCustomersWithFixedPriceCFLoading, setSetCustomersWithFixedPriceCFLoading] = useState<boolean>(false);
	const [updatePricesForExistingCustomersLoading, setUpdatePricesForExistingCustomersLoading] =
		useState<boolean>(false);
	const [fixCompaniesAndCustomersLoading, setFixCompaniesAndCustomersLoading] = useState<boolean>(false);
	const confirmation = useConfirmation();

	const fixPartner = async () => {
		setFixPartnerLoading(true);
		try {
			await adminApi.fixPartnerSubscription();
			addSuccessFlash(t("modules.tool.field.fixed.title"));
		} catch (err) {
			handleError.alert(err, addFlash);
		}
		setFixPartnerLoading(false);
	};

	const fixCompanyName = async () => {
		try {
			await confirmation({
				title: t("lib:confirmation.title"),
			});
			setFixCompanyNameLoading(true);
			await adminApi.fixCompanyName();
			addSuccessFlash(t("modules.tool.field.fixed.title"));
		} catch (err) {
			handleError.alert(err, addFlash);
		}
		setFixCompanyNameLoading(false);
	};

	const fetchWrongInvoices = async () => {
		setWrongInvoicesLoading(true);
		try {
			const res = await adminApi.getWrongInvoices();
			setWrongInvoices(res);
		} catch (err) {
			handleError.alert(err, addFlash);
		}
		setWrongInvoicesLoading(false);
	};

	const fetchAnomaliesPartnerSettlements = async () => {
		setAnomaliesPartnerSettlementsLoading(true);
		try {
			const res = await adminApi.getAnomaliesPartnerSettlements();
			setAnomaliesPartnerSettlements(res);
		} catch (err) {
			handleError.alert(err, addFlash);
		}
		setAnomaliesPartnerSettlementsLoading(false);
	};

	const clearCache = async () => {
		setClearCacheLoading(true);
		try {
			await adminApi.clearCache();
			addSuccessFlash(t("common.flash.completed", { ns: "lib" }));
		} catch (err) {
			handleError.alert(err, addFlash);
		}
		setClearCacheLoading(false);
	};

	const downloadChargebee = async () => {
		try {
			await confirmation({
				title: t("lib:confirmation.title"),
			});
			setDownloadChargebeeLoading(true);
			await adminApi.fetchChargebee();
			addSuccessFlash(t("modules.tool.field.fixed.title"));
		} catch (err) {
			handleError.alert(err, addFlash);
		}
		setDownloadChargebeeLoading(false);
	};

	const updateCF = async () => {
		try {
			await confirmation({
				title: t("lib:confirmation.title"),
			});
			setUpdateCFLoading(true);
			await adminApi.updateOrganizationCf();
			addSuccessFlash(t("modules.tool.field.fixed.title"));
		} catch (err) {
			handleError.alert(err, addFlash);
		}
		setUpdateCFLoading(false);
	};

	const rebuildPriceListItem = async () => {
		try {
			await confirmation({
				title: t("lib:confirmation.title"),
			});
			setRebuildPriceListItemCFLoading(true);
			await adminApi.rebuildPriceListItem();
			addSuccessFlash(t("modules.tool.field.fixed.title"));
		} catch (err) {
			handleError.alert(err, addFlash);
		}
		setRebuildPriceListItemCFLoading(false);
	};

	const setCustomersWithFixedPrice = async () => {
		try {
			await confirmation({
				title: t("lib:confirmation.title"),
			});
			setSetCustomersWithFixedPriceCFLoading(true);
			await adminApi.setCustomersWithFixedPrice();
			addSuccessFlash(t("modules.tool.field.fixed.title"));
		} catch (err) {
			handleError.alert(err, addFlash);
		}
		setSetCustomersWithFixedPriceCFLoading(false);
	};

	const updatePricesForExistingCustomers = async () => {
		try {
			await confirmation({
				title: t("lib:confirmation.title"),
			});
			setUpdatePricesForExistingCustomersLoading(true);
			await adminApi.updatePricesForExistingCustomers();
			addSuccessFlash(t("modules.tool.field.fixed.title"));
		} catch (err) {
			handleError.alert(err, addFlash);
		}
		setUpdatePricesForExistingCustomersLoading(false);
	};

	const fixCompaniesAndCustomers = async () => {
		try {
			await confirmation({
				title: t("lib:confirmation.title"),
			});
			setFixCompaniesAndCustomersLoading(true);
			await adminApi.fixCompaniesAndCustomers();
			addSuccessFlash(t("modules.tool.field.fixed.title"));
		} catch (err) {
			handleError.alert(err, addFlash);
		}
		setFixCompaniesAndCustomersLoading(false);
	};

	return (
		<FormDirty isDirty={false}>
			<p>
				<Link to={"/admin/tools/product_catalog"} className={"btn btn-default"}>
					{t("modules.tool.field.subscriptions_price_list.title")}
				</Link>
			</p>
			<p>
				<ButtonLoading loading={fixPartnerLoading} onClick={fixPartner}>
					{t("modules.tool.action.fix_partners_id.title")}
				</ButtonLoading>
			</p>
			<p>
				<ButtonLoading loading={wrongInvoicesLoading} onClick={fetchWrongInvoices}>
					{t("modules.tool.action.list_of_wrong_invoices.title")}
				</ButtonLoading>{" "}
				<ButtonLoading loading={fixCompanyNameLoading} onClick={fixCompanyName}>
					{t("modules.tool.action.fix_company_names.title")}
				</ButtonLoading>
			</p>
			<p>
				<ButtonLoading loading={anomaliesPartnerSettlementsLoading} onClick={fetchAnomaliesPartnerSettlements}>
					Anomalie rozliczeń partnerów
				</ButtonLoading>
			</p>
			<p>
				<Button onClick={() => setShowSyncCompaniesModal(true)}>
					{t("modules.tool.action.sync_companies.title")}
				</Button>
			</p>
			<p>
				<ButtonLoading loading={clearCacheLoading} onClick={clearCache}>
					{t("modules.tool.action.clear_cache.title")}
				</ButtonLoading>
			</p>
			<p>
				<ButtonLoading loading={downloadChargebeeLoading} onClick={downloadChargebee}>
					Zaaktualizuj lokalizacje na podstawie chargebee
				</ButtonLoading>
			</p>
			<p>
				<ButtonLoading loading={updateCFLoading} onClick={updateCF}>
					Zaaktualizuj CF lokalizacji (partner keeper, priority test itp.)
				</ButtonLoading>
			</p>
			<p>
				<ButtonLoading loading={rebuildPriceListItemCFLoading} onClick={rebuildPriceListItem}>
					CENNIK (step1) - aktualizuj PriceListItem z Chargebee
				</ButtonLoading>
			</p>
			<p>
				<ButtonLoading loading={setCustomersWithFixedPriceCFLoading} onClick={setCustomersWithFixedPrice}>
					CENNIK (step2) - oznacz Customerów z niestandardową ceną (customField na Chargebee)
				</ButtonLoading>
			</p>
			<p>
				<ButtonLoading
					loading={updatePricesForExistingCustomersLoading}
					onClick={updatePricesForExistingCustomers}
				>
					CENNIK (step3) - aktualizuj ceny Customerów z standardową ceną
				</ButtonLoading>
			</p>
			<p>
				<ButtonLoading loading={fixCompaniesAndCustomersLoading} onClick={fixCompaniesAndCustomers}>
					Napraw dane w Intercom
				</ButtonLoading>
			</p>
			{wrongInvoices && <AdminToolsWrongInvoicesTable wrongInvoices={wrongInvoices} />}
			{anomaliesPartnerSettlements && (
				<AdminToolsAnomaliesPartnerSettlements anomaliesPartnerSettlements={anomaliesPartnerSettlements} />
			)}
			{showSyncCompaniesModal && (
				<SyncCompaniesModalForm
					isShown={showSyncCompaniesModal}
					onHide={() => setShowSyncCompaniesModal(false)}
				/>
			)}
		</FormDirty>
	);
};

export default AdminToolsForm;
